import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table, Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';

import { fetchWarehouses, deleteWarehouse } from '../../../store/actions/warehouse';

import EditWarehouse from './edit';
import AddWarehouse from './add';
import CustomModal from '../../../hoc/customModal';

import { PLAN_LIMITS } from '../../../config/plans'; 

// Assets 
import { ReactComponent as EditIcon } from '../../../assets/edit.svg';
import { ReactComponent as RemoveIcon } from '../../../assets/remove.svg';

// Utils
import { Loader, isSubscriptionActive, shouldShowPaymentAlert } from '../../../utils/tools';
import PaymentAlert from '../../../utils/paymentAlert';
import PlanLimitAlert from '../../../utils/planLimitAlert';

const Warehouse = () => {
    const dispatch = useDispatch();

    const warehouses = useSelector(state => state.branch.warehouse);
    const currentBranch = useSelector(state => state.branch.current);
    const deletedWarehouse = useSelector(state => state.branch.deletedWarehouse);
    const tenant = useSelector(state => state.tenant.current);

    const planLimits = PLAN_LIMITS[tenant.plan];
    const isActive = isSubscriptionActive(tenant?.subscriptionStatus, tenant?.plan);
    const isAtLimit = !!(tenant?.plan && warehouses && Array.isArray(warehouses) && warehouses.length >= planLimits?.maxWarehouses);

    // Custom modals state
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [warehouseToEdit, setWarehouseToEdit] = useState(null);

    // Confirmation modal state
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [warehouseToDelete, setWarehouseToDelete] = useState(null);

    useEffect(() => {
        if (currentBranch !== '') {
            dispatch(fetchWarehouses(currentBranch));
        }
    }, [dispatch, currentBranch, deletedWarehouse]);

    // Handlers
    const handleEditButtonClick = (warehouse) => {
        setWarehouseToEdit(warehouse);
        setIsEditModalVisible(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalVisible(false);
        setWarehouseToEdit(null);
    };

    const handleCloseAddModal = () => {
        setIsAddModalVisible(false);
    };

    const handleAddButtonClick = () => {
        setIsAddModalVisible(true);
    };

    const handleDeleteButtonClick = (warehouse) => {
        setWarehouseToDelete(warehouse);
        setShowConfirmModal(true);
    };

    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false);
        setWarehouseToDelete(null);
    };

    const handleConfirmDelete = () => {
        if (warehouseToDelete && currentBranch !== '') {
            dispatch(deleteWarehouse({ branchId: currentBranch, warehouse: warehouseToDelete }))
                .then(() => {
                    if (currentBranch !== '') {
                        dispatch(fetchWarehouses(currentBranch));
                    }
                    setShowConfirmModal(false);
                })
                .catch(error => {
                    console.error('Error deleting warehouse:', error);
                    setShowConfirmModal(false);
                });
        }
    };

    // Render Tooltips
    const renderTooltipEdit = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Upraviť sklad
        </Tooltip>
    );

    const renderTooltipRemove = (props) => (
        <Tooltip id="button-tooltip" {...props}>
            Vymazať sklad
        </Tooltip>
    );

    return (
        <>  
            {currentBranch.loading ? (
                <Loader/>
            ) : (
                <div>
                    <Button 
                        className="button-custom-large radius"
                        onClick={handleAddButtonClick}
                        disabled={tenant.plan && warehouses && Array.isArray(warehouses) && warehouses.length >= planLimits.maxWarehouses || !isActive}
                    >
                        Pridať sklad
                    </Button>

                    {tenant.plan && warehouses && Array.isArray(warehouses) && warehouses.length >= planLimits.maxWarehouses && (
                        <PlanLimitAlert
                            current={warehouses.length}
                            max={planLimits.maxWarehouses}
                            label="skladov"
                            plan={tenant.plan}
                        />
                    )}

                    {tenant && !isAtLimit && shouldShowPaymentAlert(tenant.subscriptionStatus, tenant.plan) && (
                        <PaymentAlert status={tenant.subscriptionStatus} plan={tenant.plan} />
                    )}

                    <div className='content pt-4 pb-4'>
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th className="px-3">Sklad</th>
                                    <th className="rounded-top-right custom-table-header-color">Akcia</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(warehouses) && warehouses.length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="text-center text-muted py-5">
                                            <p className="mb-1"><strong>Zatiaľ nemáte žiadny sklad.</strong></p>
                                            <p className="mb-0">Kliknutím na tlačidlo <em>Pridať sklad</em> vytvoríte prvý sklad.</p>
                                        </td>
                                    </tr>
                                )}
                                {Array.isArray(warehouses) && warehouses.map((warehouse) => (
                                    <tr key={warehouse}>
                                        <td className="px-3">{warehouse}</td>
                                        <td>

                                            {/* Edit Icon */}
                                            <Button
                                                className="btnIcon"
                                            >
                                                <OverlayTrigger placement="top" overlay={renderTooltipEdit}>
                                                    <EditIcon
                                                        onClick={() => handleEditButtonClick(warehouse)}
                                                        alt="Upraviť"
                                                        className="icon icon-edit"
                                                    />
                                                </OverlayTrigger>
                                            </Button>
                                            
                                            {/* Remove Icon */}
                                            <Button
                                                className="btnIcon"
                                            >
                                                <OverlayTrigger placement="top" overlay={renderTooltipRemove}>
                                                    <RemoveIcon
                                                        onClick={() => handleDeleteButtonClick(warehouse)}
                                                        alt="Vymazať"
                                                        className="icon icon-remove"
                                                    />
                                                </OverlayTrigger>
                                            </Button>

                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {/* Custom modal for editing warehouse */}
                    <CustomModal isVisible={isEditModalVisible} onClose={handleCloseEditModal}>
                        {
                            warehouseToEdit && isEditModalVisible ? 
                                <EditWarehouse warehouse={warehouseToEdit} onClose={handleCloseEditModal} />
                            : null
                        }
                    </CustomModal>


                    {/* Custom modal for adding warehouse */}
                    <CustomModal isVisible={isAddModalVisible} onClose={handleCloseAddModal}>
                        <AddWarehouse onClose={handleCloseAddModal} />
                    </CustomModal>

                    {/* Confirmation modal */}
                    <Modal
                        show={showConfirmModal}
                        onHide={handleCloseConfirmModal}
                        centered
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Potvrdenie</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            Naozaj chcete vymazať sklad <b>{warehouseToDelete}</b>?
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseConfirmModal}>
                                Zrušiť
                            </Button>
                            <Button variant="danger" onClick={handleConfirmDelete}>
                                Áno, vymazať
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>
            )}
        </>
    );
};

export default Warehouse;
